import { useCallback, useContext, useMemo, useState } from "react";
import { compareWithTolerance } from "../../../../utilities/compare-with-tolerance";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { isValidMoneyValue } from "../../../../utilities/is-valid-money";
import {
  cashRoundingPaymentMethod,
  round2Decimal,
} from "../../../../utilities/round-cash";
import {
  PaymentControllerContext,
  PaymentControllerContextType,
} from "./context";
import { AddPaymentOptions, PaymentControllerOptions } from "./types";
import { moneyStringToCents } from "../../../../utilities/money-string-to-cents";

export const usePaymentController = ({
  sale,
  roundingPaymentMethod: pRoundingPaymentMethod,
  changePaymentMethod: pChangePaymentMethod,
  defaultTenderedAmount = "0.00",
}: PaymentControllerOptions): PaymentControllerContextType => {
  const { productSurchargeId } = useGlobalSettingsProvider();

  const [roundingPaymentMethod] = useState<PaymentMethod | undefined>(
    pRoundingPaymentMethod
  );

  const [changePaymentMethod] = useState<PaymentMethod | undefined>(
    pChangePaymentMethod
  );

  const [tenderedAmountInput, setTenderedAmountInput] = useState<string>(
    defaultTenderedAmount
  );

  const handleSetTenderedAmountInput = useCallback(
    (value: string) => {
      setTenderedAmountInput(value);
    },
    [setTenderedAmountInput]
  );

  const validTenderedAmount = useMemo(() => {
    return isValidMoneyValue(tenderedAmountInput);
  }, [tenderedAmountInput]);

  const tenderedAmountAsInteger = useMemo(() => {
    if (validTenderedAmount) {
      return moneyStringToCents(tenderedAmountInput);
    }
    return undefined;
  }, [tenderedAmountInput, validTenderedAmount]);

  const addPayment = useCallback(
    async (
      paymentMethod: PaymentMethod,
      options?: AddPaymentOptions
    ): Promise<void> => {
      if (!sale) {
        throw new Error("Sale is not provided");
      }

      // do some checks to make sure the payment is a valid number
      const tenderedAmount =
        options?.tenderedAmount !== undefined
          ? options.tenderedAmount
          : parseFloat(tenderedAmountInput);

      const validTenderedAmount = isValidMoneyValue(tenderedAmount.toString());

      if (!validTenderedAmount) {
        throw new Error(`The amount is not a valid number [${tenderedAmount}]`);
      }

      const normalizedRoundingPaymentMethod = options?.roundingPaymentMethod
        ? options.roundingPaymentMethod
        : roundingPaymentMethod;

      const normalizedChangePaymentMethod = options?.changePaymentMethod
        ? options.changePaymentMethod
        : changePaymentMethod;

      if (!normalizedRoundingPaymentMethod || !normalizedChangePaymentMethod) {
        throw new Error(
          "Rounding payment method or change payment method is not provided"
        );
      }

      const isCash = paymentMethod.payType.id === "1";
      const balance = await sale.balance_ASYNC();
      const total = (await sale.totals_ASYNC()).totalPrice;

      // for the moment assume if cash, then must be rounding and change given
      if (true === isCash) {
        // round amount to the nearest 0.05
        const roundedAmount = cashRoundingPaymentMethod(
          tenderedAmount,
          paymentMethod
        );

        if (false === compareWithTolerance(roundedAmount, tenderedAmount)) {
          throw new Error("The amount needs to be rounded to the nearest 0.05");
        }

        const roundedBalance = cashRoundingPaymentMethod(
          balance,
          paymentMethod
        );

        // only give change if the amount is positive and the rounded amount is less than the rounded balance
        // also dont give change if the sale total is <= 0
        const change =
          total > 0 && roundedBalance > 0 && roundedAmount > roundedBalance
            ? round2Decimal(
                roundedAmount > 0 ? roundedAmount - roundedBalance : 0
              )
            : 0;

        const willCloseSale = compareWithTolerance(
          roundedBalance - (roundedAmount - change),
          0
        );

        const remainder = willCloseSale
          ? round2Decimal(balance - roundedBalance)
          : 0;

        await sale.addPayment({
          amount: tenderedAmount,
          paymentMethodId: paymentMethod.id,
          paymentDate: new Date(),
          metadata: options?.metadata,
        });

        if (remainder > 0 || remainder < 0) {
          await sale.addPayment({
            amount: remainder,
            paymentMethodId: normalizedRoundingPaymentMethod.id,
            paymentState: "Settled",
            paymentDate: new Date(),
          });
        }

        if (change > 0) {
          await sale.addPayment({
            amount: change * -1,
            paymentState: "Settled",
            paymentMethodId: normalizedChangePaymentMethod.id,
            // make sure the change payment is after the sale payment
            paymentDate: new Date(
              new Date().setSeconds(new Date().getSeconds() + 2)
            ),
          });
        }
      } else {
        await sale.addPayment({
          paymentState: options?.paymentState ?? "Settled",
          amount: tenderedAmount,
          name: options?.name ?? undefined,
          paymentMethodId: paymentMethod.id,
          paymentDate: new Date(),
          transactionId: options?.transactionId,
          metadata: options?.metadata,
        });
      }

      if (options?.surcharge) {
        if (!productSurchargeId) {
          throw new Error("Product surcharge id is not provided");
        }
        const c = `SUSUSUSU${paymentMethod.retailerPayCode || ""}`;
        await sale.addItem({
          productId: productSurchargeId,
          quantity: 1,
          basePrice: options.surcharge,
          taxRatePercent: 0,
          discount: 0,
          stockCode: c,
          stockNumber: c,
          metadata: {
            surcharge: true,
            surchargeDescription: `${paymentMethod.name} surcharge`,
            paymentTypeId: paymentMethod.payType.id,
            paymentMethodId: paymentMethod.id,
          },
        });
      }
    },
    [
      pRoundingPaymentMethod,
      pChangePaymentMethod,
      roundingPaymentMethod,
      changePaymentMethod,
      tenderedAmountInput,
      validTenderedAmount,
      sale,
    ]
  );

  const isRefundAmount = useMemo(() => {
    if (validTenderedAmount) {
      return parseFloat(tenderedAmountInput) < 0;
    }
    return false;
  }, [validTenderedAmount, tenderedAmountInput]);

  const context: PaymentControllerContextType = useMemo(
    () => ({
      tenderedAmountInput,
      validTenderedAmount,
      setTenderedAmountInput: handleSetTenderedAmountInput,
      addPayment,
      isRefundAmount,
      tenderedAmountAsInteger,
    }),
    [
      tenderedAmountInput,
      validTenderedAmount,
      setTenderedAmountInput,
      addPayment,
      isRefundAmount,
      tenderedAmountAsInteger,
    ]
  );

  return context;
};

export const usePaymentControllerProvider = () => {
  const context = useContext(PaymentControllerContext);
  if (!context) {
    throw new Error("No PaymentControllerContext context was provided. ");
  }

  return context;
};
